import React from 'react';
import {
  FormControl,
  Stack,
  TextField,
  Avatar,
  Box,
  Typography,
  Chip,
  Autocomplete,
} from '@mui/material';
import InlineWarning from './common/InlineWarning';
import InlineContactForm from './common/InlineContactForm';
import type { SchedulerContact } from '../hooks/useMeetingSchedulerData';

interface Advisor {
  _id: string;
  firstName: string;
  lastName: string;
  email?: string;
}

interface Client {
  _id: string;
  name: string;
  entityType?: string;
}

interface ClientContactSelectorProps {
  context: 'client' | 'global';
  mode: 'create' | 'edit' | 'upload';
  clients: Client[];
  linkedContacts: SchedulerContact[];
  isContactsLoading: boolean;
  filteredAdvisors: Advisor[];
  selectedContactsObjects: SchedulerContact[];
  selectedAdvisorsObjects: Advisor[];
  selectedClient: string;
  contactSearch: string;
  advisorSearch: string;
  clientSearch: string;
  errors: { selectedClient: boolean; selectedContacts: boolean };
  onClientChange: (clientId: string, contacts: SchedulerContact[]) => void;
  onContactsChange: (contactIds: string[]) => void;
  onAdvisorsChange: (advisorIds: string[]) => void;
  onSearchChange: (field: 'clientSearch' | 'contactSearch' | 'advisorSearch', value: string) => void;
  onClose: () => void;
  onContactsRefresh: (clientId: string) => void;
  getInitials: (name: string) => string;
}

const ClientContactSelector: React.FC<ClientContactSelectorProps> = ({
  context,
  mode,
  clients,
  linkedContacts,
  isContactsLoading,
  filteredAdvisors,
  selectedContactsObjects,
  selectedAdvisorsObjects,
  selectedClient,
  contactSearch,
  advisorSearch,
  clientSearch,
  errors,
  onClientChange,
  onContactsChange,
  onAdvisorsChange,
  onSearchChange,
  onClose,
  onContactsRefresh,
  getInitials,
}) => (
  <>
    {context === 'global' && mode !== 'edit' && (
      <FormControl fullWidth required error={errors.selectedClient}>
        <Autocomplete
          options={clients}
          getOptionLabel={(option) => (typeof option === 'string' ? option : option.name || '')}
          value={clients.find((c) => c._id === selectedClient) || null}
          onChange={async (_, value) => {
            if (value) {
              onClientChange(value._id, []);
            } else {
              onClientChange('', []);
            }
          }}
          inputValue={clientSearch}
          onInputChange={(_, value) => onSearchChange('clientSearch', value)}
          filterOptions={(options, params) => {
            const q = params.inputValue.toLowerCase();
            return options.filter((o) => (o.name || '').toLowerCase().includes(q));
          }}
          getOptionKey={(option) => option._id}
          renderInput={(params) => (
            <TextField
              {...params}
              label="Client"
              placeholder="Type to search..."
              error={errors.selectedClient}
              helperText={errors.selectedClient ? 'Client required' : ''}
            />
          )}
          renderOption={(props, option) => (
            <li {...props}>
              <Box sx={{ display: 'flex', alignItems: 'center', width: '100%', gap: 1.5 }}>
                <Avatar sx={{ width: 32, height: 32, fontSize: '0.75rem', flexShrink: 0 }}>
                  {getInitials(option.name || '')}
                </Avatar>
                <Typography variant="body2" sx={{ fontWeight: 500 }}>{option.name}</Typography>
              </Box>
            </li>
          )}
        />
      </FormControl>
    )}

    {context === 'global' && clients.length === 0 && (
      <InlineWarning
        message="You have no clients yet. Create a client first to schedule a meeting."
        action={{ label: 'Create a client', onClick: onClose }}
      />
    )}

    {selectedClient && linkedContacts.length === 0 && !isContactsLoading && mode !== 'edit' && (() => {
      const selClient = clients.find((c) => c._id === selectedClient);
      return (
        <InlineContactForm
          key={selectedClient}
          clientId={selectedClient}
          clientName={selClient?.name}
          entityType={selClient?.entityType}
          existingLinkedContacts={linkedContacts}
          onSuccess={() => {
            window.dispatchEvent(new Event('contactsUpdated'));
            onContactsRefresh(selectedClient);
          }}
        />
      );
    })()}

    <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
      <FormControl fullWidth required error={errors.selectedContacts} sx={{ opacity: selectedClient ? 1 : 0.6 }}>
        <Autocomplete
          multiple
          freeSolo={false}
          disablePortal={false}
          openOnFocus
          disabled={!selectedClient}
          options={linkedContacts}
          getOptionLabel={(option) => (typeof option === 'string' ? option : option.name || '')}
          isOptionEqualToValue={(option, value) => option._id === value._id}
          value={selectedContactsObjects}
          onChange={(_, value) => onContactsChange(value.map((c) => c._id))}
          inputValue={contactSearch}
          onInputChange={(_, value) => onSearchChange('contactSearch', value)}
          filterOptions={(options, params) => {
            const q = params.inputValue.toLowerCase().trim();
            if (!q) return options;
            return options.filter((o) =>
              (o.name || '').toLowerCase().includes(q) || (o.email || '').toLowerCase().includes(q),
            );
          }}
          noOptionsText={!selectedClient ? 'Select a client first' : 'No contacts available'}
          getOptionKey={(option) => option._id}
          renderInput={(params) => (
            <TextField
              {...params}
              label="Contacts"
              placeholder={!selectedClient ? 'Select a client first' : 'Type to search...'}
              error={errors.selectedContacts}
              helperText={errors.selectedContacts ? 'At least one contact required' : ''}
            />
          )}
          renderTags={(selected, getTagProps) =>
            selected.map((contact, index) => (
              <Chip key={contact._id} label={contact.name} size="small" {...getTagProps({ index })} />
            ))
          }
          renderOption={(props, option) => (
            <li {...props}>
              <Box sx={{ display: 'flex', alignItems: 'center', width: '100%', gap: 1.5 }}>
                <Avatar sx={{ width: 32, height: 32, fontSize: '0.75rem', flexShrink: 0 }}>
                  {getInitials(option.name || '')}
                </Avatar>
                <Box sx={{ flex: 1 }}>
                  <Typography variant="body2" sx={{ fontWeight: 500 }}>{option.name}</Typography>
                  {option.email && (
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                      {option.email}
                    </Typography>
                  )}
                </Box>
              </Box>
            </li>
          )}
        />
      </FormControl>

      <FormControl fullWidth>
        <Autocomplete
          multiple
          freeSolo={false}
          disablePortal={false}
          openOnFocus
          options={filteredAdvisors}
          getOptionLabel={(option) =>
            typeof option === 'string' ? option : `${option.firstName} ${option.lastName}` || ''
          }
          isOptionEqualToValue={(option, value) => option._id === value._id}
          value={selectedAdvisorsObjects}
          onChange={(_, value) => onAdvisorsChange(value.map((a) => a._id))}
          inputValue={advisorSearch}
          onInputChange={(_, value) => onSearchChange('advisorSearch', value)}
          filterOptions={(options, params) => {
            const q = params.inputValue.toLowerCase().trim();
            if (!q) return options;
            return options.filter((o) => {
              if (!o) return false;
              const full = `${o.firstName || ''} ${o.lastName || ''}`.toLowerCase();
              return (
                full.includes(q) ||
                (o.email || '').toLowerCase().includes(q)
              );
            });
          }}
          noOptionsText="No advisors available"
          getOptionKey={(option) => option._id}
          renderInput={(params) => (
            <TextField {...params} label="Advisors" placeholder="Type to search..." />
          )}
          renderTags={(selected, getTagProps) =>
            selected.map((advisor, index) => (
              <Chip
                key={advisor._id}
                label={`${advisor.firstName} ${advisor.lastName}`}
                size="small"
                {...getTagProps({ index })}
              />
            ))
          }
          renderOption={(props, option) => (
            <li {...props}>
              <Box sx={{ display: 'flex', alignItems: 'center', width: '100%', gap: 1.5 }}>
                <Avatar sx={{ width: 32, height: 32, fontSize: '0.75rem', flexShrink: 0 }}>
                  {(option.firstName?.[0] || '').toUpperCase()}
                  {(option.lastName?.[0] || '').toUpperCase()}
                </Avatar>
                <Box sx={{ flex: 1 }}>
                  <Typography variant="body2" sx={{ fontWeight: 500 }}>
                    {`${option.firstName} ${option.lastName}`}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">{option.email}</Typography>
                </Box>
              </Box>
            </li>
          )}
        />
      </FormControl>
    </Stack>
  </>
);

export default ClientContactSelector;
